import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Newspaper } from "lucide-react";
import { api } from "../api";
import { useApp } from "../store";

export default function NewsPage() {
  const { t, lang } = useApp();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/news").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  return (
    <div
      className="min-h-screen text-white grain relative theme-public"
      style={{ background: "var(--sec-actualites-bg, #0A1428)" }}
      data-testid="news-page"
    >
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-24 relative z-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[var(--accent-soft)] flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-[var(--accent-soft)]" />
            <Newspaper size={14} />
            {t("nav_news")}
          </p>
          <h1 data-testid="news-page-title" className="mt-5 font-display font-semibold text-3xl sm:text-4xl tracking-tight">
            {t("news_title")}
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/55">{t("news_sub")}</p>
          <div className="w-16 h-px bg-[var(--accent)] my-10" />
        </motion.div>

        {items === null ? null : items.length === 0 ? (
          <p data-testid="news-empty" className="font-display italic text-xl text-white/45 py-10">
            {t("news_empty")}
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            {items.map((n, i) => (
              <motion.article
                key={n.id}
                data-testid={`news-item-${n.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: Math.min(i, 5) * 0.08 }}
                className="border border-[var(--accent-soft)]/15 bg-white/[0.03] overflow-hidden"
              >
                {n.image && (
                  <div className="bg-black/25 flex items-center justify-center">
                    <img src={n.image} alt={lang === "fr" ? n.title_fr : n.title_en || n.title_fr} loading="lazy" className="w-full h-auto max-h-[520px] object-contain" />
                  </div>
                )}
                <div className="p-6 sm:p-8">
                  <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[var(--accent-soft)]">
                    {t("news_published")} {new Date(n.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                  <h2 className="mt-3 font-display font-semibold text-2xl">{lang === "fr" ? n.title_fr : n.title_en || n.title_fr}</h2>
                  <p className="mt-3 text-sm sm:text-base text-white/70 leading-relaxed whitespace-pre-wrap">
                    {lang === "fr" ? n.body_fr : n.body_en || n.body_fr}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <Link
          data-testid="news-back-link"
          to="/"
          className="mt-14 inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-[var(--accent-soft)] transition-colors duration-300"
        >
          <ArrowLeft size={13} />
          {t("login_back")}
        </Link>
      </div>
    </div>
  );
}
